import { useEffect, useState } from 'react';

import { useUserStore, userStoreSelector } from '@editor/stores/userStore';
import { TeamSubscriptionPlan } from '@repo/domain/team';
import { SubscriptionPlan, User } from '@repo/domain/user';

type UserAccount = {
  slug: string | null;
  name: string | null;
  profileImage?: string;
  plan?: SubscriptionPlan | TeamSubscriptionPlan;
  active?: boolean;
  isMainAccount?: boolean;
};

// This will return the required main account user info that needs to be displayed in the team selector
const constructMainUserAccountInfoObj = (user: Partial<User>) => {
  return {
    slug: user.slug,
    name: user.name,
    profileImage: user.profileImage,
    plan: user.plan,
    active: true,
    isMainAccount: true,
  };
};

/**
 * This hook is very specific to the TeamSelector component, however it has been moved into
 * its own file because we need to use it in the WorkspaceOrTeamSelector component to determine
 * which selector to render.
 *
 * @returns an array of all the user accounts (main + team accounts) or null if the user is not logged in
 */
export const useTeamsForTeamSelector = () => {
  const user = useUserStore(userStoreSelector.user);

  const [allAccounts, setAllAccounts] = useState<UserAccount[]>([]);

  // Get main user account + all the active team accounts whenever the user value updates
  useEffect(() => {
    // The below condition is met when the user is logged in as a main account (non-team)
    if (user?.teams && user.teams.length > 0) {
      const mainUserAccount = constructMainUserAccountInfoObj(user.profile);
      // @ts-expect-error - The user plan & team account plan don't exactly match but in this case it is fine
      setAllAccounts(user.teams.concat(mainUserAccount));
    } else if (user?.isTeam && user.user) {
      // this condition is met when the user has switched to a team account (non-main)
      const loggedInUser = user.user;
      const mainUserAccount = constructMainUserAccountInfoObj(loggedInUser);
      if (loggedInUser.teams) {
        // @ts-expect-error - The user plan & team account plan don't exactly match but in this case it is fine
        setAllAccounts(loggedInUser.teams.concat(mainUserAccount));
      }
    }
  }, [user]);
  if (!user?.teams || allAccounts.length < 1) {
    return null;
  }

  return allAccounts;
};
